import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { API, setAuthToken, setCurrentUser, setGymData } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { CountryCodeSelector } from '../components/CountryCodeSelector';
import {
  Dumbbell,
  Mail,
  Lock,
  User,
  Building2,
  Phone,
  Eye,
  EyeOff,
  ArrowRight,
  TrendingUp,
  ShieldCheck,
  Zap,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

export default function GymRegistrationPage() {
  const [form, setForm] = useState({
    owner_name: '',
    gym_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    country_code: '+1',
    agreed_to_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { setUser, setGym } = useAuth();

  const setField = key => e => {
    setForm(p => ({ ...p, [key]: e.target.value }));
    if (errors[key]) {
      setErrors(p => ({ ...p, [key]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }
    if (form.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters.';
    }
    if (!form.agreed_to_terms) {
      newErrors.terms = 'Please agree to the Terms of Service.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/gyms/register`, {
        gym_name: form.gym_name,
        owner_name: form.owner_name,
        email: form.email,
        password: form.password,
        phone: form.country_code + form.phone,
      });
      const { token, gym } = res.data;
      setAuthToken(token);
      setGymData(gym);
      const u = { role: 'gym_owner', gym_id: gym.id, email: gym.email };
      setCurrentUser(u);
      setGym(gym);
      setUser(u);
      toast.success(`Welcome, ${gym.gym_name}! Your 7-day trial has started.`);
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:h-screen flex flex-col lg:flex-row font-sans bg-[#04080a] lg:overflow-hidden">
      {/* LEFT SECTION */}
      <div className="lg:w-[46%] bg-[#04080a] relative flex flex-col px-6 py-10 lg:p-12 lg:h-full lg:overflow-hidden hide-scrollbar">
        <div className="absolute w-[400px] h-[400px] rounded-full bg-[radial-gradient(circle,rgba(16,185,129,0.08)_0%,transparent_65%)] -top-[150px] -left-[100px] pointer-events-none" />
        <div className="absolute w-[250px] h-[250px] rounded-full bg-[radial-gradient(circle,rgba(16,185,129,0.05)_0%,transparent_65%)] -bottom-[80px] -right-[60px] pointer-events-none" />

        <div className="flex items-center gap-2.5 mb-8 lg:mb-10 relative z-10 flex-shrink-0">
          <div className="w-8 h-8 lg:w-9 lg:h-9 bg-emerald-500 rounded-xl flex items-center justify-center">
            <Dumbbell size={18} className="text-white" />
          </div>
          <span className="text-lg lg:text-xl font-bold text-white tracking-tight">
            Fit<em className="not-italic text-emerald-500">xero</em>.
          </span>
        </div>

        <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1.5 mb-6 w-fit relative z-10 flex-shrink-0">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[10px] lg:text-xs text-emerald-500 font-bold tracking-wide">
            TRUSTED BY 8,000+ GYM OWNERS
          </span>
        </div>

        <div className="relative z-10 mb-8 flex-shrink-0">
          <h1 className="text-3xl lg:text-[2.5rem] font-extrabold text-white leading-[1.15] tracking-tight mb-3">
            Scale your gym.
            <br />
            <em className="not-italic text-emerald-500">Without the chaos.</em>
          </h1>
          <p className="text-xs lg:text-sm text-emerald-900/60 lg:text-emerald-50/60 leading-relaxed max-w-sm font-medium">
            The ultimate operating system built specifically for modern gym owners who want to
            maximize profit and minimize admin work.
          </p>
        </div>

        {/* MOBILE CAROUSEL & DESKTOP GRID FOR FEATURES */}
        <div className="flex-1 flex overflow-x-auto lg:flex-col lg:overflow-y-auto snap-x snap-mandatory hide-scrollbar gap-4 lg:gap-3 relative z-10 -mx-6 px-6 lg:mx-0 lg:px-0 pb-4 lg:pb-0">
          {[
            {
              icon: Zap,
              before: 'AUTOMATED BILLING',
              pain: 'Stop begging for fees. Recover 98% of lost revenue effortlessly.',
              fix: 'Auto-reminders, failed payment retries, and instant invoicing.',
            },
            {
              icon: ShieldCheck,
              before: 'MEMBER RETENTION',
              pain: "Know exactly who's at risk of canceling before they actually do.",
              fix: 'Smart churn alerts & live attendance heatmaps.',
            },
            {
              icon: TrendingUp,
              before: 'BUSINESS ANALYTICS',
              pain: 'Ditch the spreadsheets. Understand your MRR and growth instantly.',
              fix: 'Real-time revenue dashboard and financial forecasting.',
            },
          ].map((f, i) => (
            <div
              key={i}
              className="snap-center shrink-0 w-[85%] lg:w-full flex items-start gap-3.5 bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-colors rounded-2xl p-4 lg:p-5"
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-500/10 flex-shrink-0 shadow-[inset_0_0_12px_rgba(16,185,129,0.1)]">
                <f.icon size={18} className="text-emerald-500" />
              </div>
              <div className="pr-2">
                <div className="text-[10px] font-bold text-emerald-500 tracking-wider mb-1.5 uppercase">
                  {f.before}
                </div>
                <div className="text-[13px] sm:text-[14px] font-semibold text-gray-200 leading-snug mb-2">
                  {f.pain}
                </div>
                <div className="text-[11px] sm:text-[12px] font-medium text-gray-400 flex items-center gap-1.5">
                  <ArrowRight size={12} className="text-emerald-500" /> {f.fix}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3 pt-6 mt-auto border-t border-white/5 relative z-10 flex-shrink-0">
          <div className="flex -space-x-2">
            {['JM', 'SR', 'TC', 'AL', '+'].map((av, i) => (
              <div
                key={i}
                className="w-7 h-7 rounded-full border-2 border-[#04080a] flex items-center justify-center text-[9px] font-bold"
                style={{
                  background: ['#1d4ed8', '#7c3aed', '#065f46', '#92400e', '#1a1a2e'][i],
                  color: ['#bfdbfe', '#ddd6fe', '#a7f3d0', '#fde68a', '#818cf8'][i],
                }}
              >
                {av}
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-400">
            <strong className="text-emerald-500/80 font-semibold">Join 8,000+ owners</strong>{' '}
            scaling their gyms.
          </div>
        </div>
      </div>

      {/* RIGHT SECTION */}
      <div className="flex-1 bg-[#0b1015] lg:rounded-l-3xl flex flex-col px-6 py-10 lg:px-16 relative lg:-ml-6 z-20 lg:shadow-[-20px_0_50px_rgba(0,0,0,0.5)] lg:h-full lg:overflow-y-auto">
        <div className="max-w-[480px] w-full mx-auto py-6 lg:my-auto">
          <div className="hidden lg:flex items-center gap-2 mb-8">
            <div className="w-7 h-7 bg-emerald-500 rounded-lg flex items-center justify-center">
              <Dumbbell size={14} className="text-white" />
            </div>
            <span className="text-[15px] font-bold text-white tracking-tight">
              Fit<em className="not-italic text-emerald-500">xero</em>.
            </span>
          </div>

          <h2 className="text-[26px] lg:text-3xl font-extrabold text-white mb-2 tracking-tight">
            Create your account
          </h2>
          <p className="text-sm text-gray-400 mb-8 font-medium">
            Set up in 2 minutes. <strong className="text-gray-300">No credit card needed.</strong>
          </p>

          <div className="flex gap-2.5 mb-6">
            <button
              type="button"
              disabled
              className="flex-1 bg-white/[0.02] border border-white/5 rounded-xl py-2.5 flex items-center justify-center gap-2 text-xs font-semibold text-gray-500 cursor-not-allowed transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="opacity-50">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Google{' '}
              <span className="ml-1 text-[9px] bg-white/10 px-1.5 py-0.5 rounded text-gray-400">
                Soon
              </span>
            </button>
            <button
              type="button"
              disabled
              className="flex-1 bg-white/[0.02] border border-white/5 rounded-xl py-2.5 flex items-center justify-center gap-2 text-xs font-semibold text-gray-500 cursor-not-allowed transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#0A66C2" className="opacity-50">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              LinkedIn{' '}
              <span className="ml-1 text-[9px] bg-white/10 px-1.5 py-0.5 rounded text-gray-400">
                Soon
              </span>
            </button>
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-white/5"></div>
            <div className="text-[10px] font-bold text-gray-600 tracking-wider">OR</div>
            <div className="flex-1 h-px bg-white/5"></div>
          </div>

          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-x-4 gap-y-5">
            <div className="col-span-1">
              <label className="block text-[11px] sm:text-xs font-bold text-gray-300 uppercase tracking-wide mb-1.5">
                Owner name
              </label>
              <div className="relative">
                <User
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"
                />
                <input
                  required
                  value={form.owner_name}
                  onChange={setField('owner_name')}
                  placeholder="John Smith"
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl py-3 pl-10 pr-3 text-base sm:text-sm font-medium text-white focus:bg-white/[0.08] focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 shadow-sm outline-none transition-all placeholder:text-gray-500"
                />
              </div>
            </div>

            <div className="col-span-1">
              <label className="block text-[11px] sm:text-xs font-bold text-gray-300 uppercase tracking-wide mb-1.5">
                Gym name
              </label>
              <div className="relative">
                <Building2
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"
                />
                <input
                  required
                  value={form.gym_name}
                  onChange={setField('gym_name')}
                  placeholder="PowerFit Gym"
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl py-3 pl-10 pr-3 text-base sm:text-sm font-medium text-white focus:bg-white/[0.08] focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 shadow-sm outline-none transition-all placeholder:text-gray-500"
                />
              </div>
            </div>

            <div className="col-span-2">
              <label className="block text-[11px] sm:text-xs font-bold text-gray-300 uppercase tracking-wide mb-1.5">
                Work email
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"
                />
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={setField('email')}
                  placeholder="owner@gym.com"
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl py-3 pl-10 pr-3 text-base sm:text-sm font-medium text-white focus:bg-white/[0.08] focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 shadow-sm outline-none transition-all placeholder:text-gray-500"
                />
              </div>
            </div>

            <div className="col-span-1">
              <label className="block text-[11px] sm:text-xs font-bold text-gray-300 uppercase tracking-wide mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"
                />
                <input
                  type={showPw ? 'text' : 'password'}
                  required
                  value={form.password}
                  onChange={setField('password')}
                  placeholder="Min 8 chars"
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl py-3 pl-10 pr-10 text-base sm:text-sm font-medium text-white focus:bg-white/[0.08] focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 shadow-sm outline-none transition-all placeholder:text-gray-500"
                />
                <button
                  type="button"
                  aria-label="Toggle password visibility"
                  aria-expanded={showPw}
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white p-2 -mr-2"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && (
                <div className="text-red-400 text-[10px] mt-1.5 ml-1">{errors.password}</div>
              )}
            </div>

            <div className="col-span-1">
              <label className="block text-[11px] sm:text-xs font-bold text-gray-300 uppercase tracking-wide mb-1.5">
                Confirm password
              </label>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"
                />
                <input
                  type={showConfirmPw ? 'text' : 'password'}
                  required
                  value={form.confirmPassword}
                  onChange={setField('confirmPassword')}
                  placeholder="Re-enter"
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl py-3 pl-10 pr-10 text-base sm:text-sm font-medium text-white focus:bg-white/[0.08] focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 shadow-sm outline-none transition-all placeholder:text-gray-500"
                />
                <button
                  type="button"
                  aria-label="Toggle confirm password visibility"
                  aria-expanded={showConfirmPw}
                  onClick={() => setShowConfirmPw(!showConfirmPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white p-2 -mr-2"
                >
                  {showConfirmPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <div className="text-red-400 text-[10px] mt-1.5 ml-1">{errors.confirmPassword}</div>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-[11px] sm:text-xs font-bold text-gray-300 uppercase tracking-wide mb-1.5">
                Phone number
              </label>
              <div className="flex gap-2">
                <div className="bg-white/[0.05] border border-white/10 rounded-xl flex items-center overflow-hidden focus-within:border-emerald-500 focus-within:ring-4 focus-within:ring-emerald-500/20 transition-all shrink-0">
                  <CountryCodeSelector
                    value={form.country_code}
                    onChange={c => setForm(p => ({ ...p, country_code: c }))}
                    className="bg-transparent border-0 text-white hover:bg-white/5 hover:text-white focus:ring-0 shadow-none"
                  />
                </div>
                <div className="relative flex-1">
                  <Phone
                    size={16}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500"
                  />
                  <input
                    required
                    value={form.phone}
                    onChange={setField('phone')}
                    placeholder="(555) 000-0000"
                    className="w-full bg-white/[0.05] border border-white/10 rounded-xl py-3 pl-10 pr-3 text-base sm:text-sm font-medium text-white focus:bg-white/[0.08] focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 shadow-sm outline-none transition-all placeholder:text-gray-500"
                  />
                </div>
              </div>
            </div>

            <div className="col-span-2 flex items-start gap-3 mt-1">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={form.agreed_to_terms}
                  onChange={e => {
                    setForm(p => ({ ...p, agreed_to_terms: e.target.checked }));
                    setErrors(p => ({ ...p, terms: '' }));
                  }}
                  className="mt-0.5 w-4 h-4 bg-white/[0.05] border-white/20 rounded text-emerald-500 focus:ring-emerald-500 focus:ring-offset-[#0b1015] cursor-pointer"
                />
                <span className="text-xs text-gray-400 font-medium leading-relaxed group-hover:text-gray-300 transition-colors">
                  I agree to Fitxero's{' '}
                  <a
                    href="#!"
                    onClick={e => e.stopPropagation()}
                    className="text-emerald-500 font-bold hover:underline"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="#!"
                    onClick={e => e.stopPropagation()}
                    className="text-emerald-500 font-bold hover:underline"
                  >
                    Privacy Policy
                  </a>
                  . Your 7-day free trial begins immediately.
                </span>
              </label>
              {errors.terms && (
                <div className="text-red-400 text-[10px] w-full mt-1 ml-7">{errors.terms}</div>
              )}
            </div>

            <div className="col-span-2 mt-2">
              <fieldset disabled={loading} className="group">
                <button
                  type="submit"
                  className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-500/50 disabled:cursor-not-allowed text-white rounded-xl py-3.5 text-[14px] font-bold flex items-center justify-center gap-2 transition-all shadow-[0_4px_14px_rgba(16,185,129,0.3)] hover:shadow-[0_6px_20px_rgba(16,185,129,0.4)] hover:-translate-y-0.5 group-disabled:hover:translate-y-0 group-disabled:hover:shadow-none"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" /> Creating account...
                    </>
                  ) : (
                    <>
                      Create free account <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </fieldset>
            </div>
          </form>

          <p className="text-center text-sm text-gray-400 font-medium mt-8">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-emerald-500 font-bold hover:text-emerald-400 transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
